import { Component, Input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * MG3P Card Component
 * 
 * Container reutilizável para agrupar conteúdo relacionado.
 * Suporta múltiplas variantes, elevações e slots para header/footer.
 * 
 * @example
 * ```html
 * <mg3p-card title="Informações do Programa" subtitle="Dados gerais">
 *   <div card-actions>
 *     <mg3p-button variant="ghost" size="sm">Editar</mg3p-button>
 *   </div>
 *   
 *   <p>Conteúdo do card...</p>
 *   
 *   <div card-footer>
 *     <small>Atualizado em 03/06/2026</small>
 *   </div>
 * </mg3p-card>
 * ```
 */
@Component({
  selector: 'mg3p-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <article [class]="cardClasses()" [attr.role]="role">
      @if (title || subtitle || hasActions) {
        <header class="mg3p-card__header">
          <div class="mg3p-card__header-content">
            @if (title) {
              <h3 class="mg3p-card__title">{{ title }}</h3>
            }
            @if (subtitle) {
              <p class="mg3p-card__subtitle">{{ subtitle }}</p>
            }
          </div>
          @if (hasActions) {
            <div class="mg3p-card__actions">
              <ng-content select="[card-actions]"></ng-content>
            </div>
          }
        </header>
      }
      
      <div class="mg3p-card__body">
        <ng-content></ng-content>
      </div>
      
      @if (hasFooter) {
        <footer class="mg3p-card__footer">
          <ng-content select="[card-footer]"></ng-content>
        </footer>
      }
    </article>
  `,
  styles: [`
    :host {
      display: block;
    }

    .mg3p-card {
      /* Layout */
      display: flex;
      flex-direction: column;
      
      /* Visual */
      background: var(--color-bg-primary);
      border-radius: var(--radius-lg);
      
      /* Transition */
      transition: all var(--transition-base);
    }

    /* ========================================
       VARIANTS
       ======================================== */
    
    /* Default */
    .mg3p-card--default {
      border: 1px solid var(--color-border-light);
    }

    /* Elevated */
    .mg3p-card--elevated {
      border: 1px solid var(--color-border-light);
      box-shadow: var(--shadow-md);
    }

    .mg3p-card--elevated:hover {
      box-shadow: var(--shadow-lg);
      transform: translateY(-2px);
    }

    /* Outlined */
    .mg3p-card--outlined {
      border: 2px solid var(--color-border-default);
    }

    /* Flat */
    .mg3p-card--flat {
      background: var(--color-bg-secondary);
    }

    /* ========================================
       PADDING
       ======================================== */
    
    .mg3p-card--padding-none .mg3p-card__body {
      padding: 0;
    }

    .mg3p-card--padding-sm .mg3p-card__body {
      padding: var(--space-sm);
    }

    .mg3p-card--padding-md .mg3p-card__body {
      padding: var(--space-md);
    }

    .mg3p-card--padding-lg .mg3p-card__body {
      padding: var(--space-lg);
    }

    /* ========================================
       HEADER
       ======================================== */
    
    .mg3p-card__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-md);
      padding: var(--space-md);
      border-bottom: 1px solid var(--color-border-light);
    }

    .mg3p-card__header-content {
      flex: 1;
      min-width: 0;
    }

    .mg3p-card__title {
      margin: 0;
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      line-height: var(--line-height-tight);
    }

    .mg3p-card__subtitle {
      margin: var(--space-xs) 0 0;
      font-size: var(--font-size-sm);
      color: var(--color-text-muted);
      line-height: var(--line-height-normal);
    }

    .mg3p-card__actions {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      flex-shrink: 0;
    }

    /* ========================================
       BODY
       ======================================== */
    
    .mg3p-card__body {
      flex: 1;
      min-height: 0;
    }

    /* ========================================
       FOOTER
       ======================================== */
    
    .mg3p-card__footer {
      padding: var(--space-md);
      border-top: 1px solid var(--color-border-light);
      background: var(--color-bg-secondary);
      border-radius: 0 0 var(--radius-lg) var(--radius-lg);
    }

    /* ========================================
       INTERACTIVE
       ======================================== */
    
    .mg3p-card--interactive {
      cursor: pointer;
    }

    .mg3p-card--interactive:hover {
      border-color: var(--color-primary-500);
      box-shadow: var(--shadow-lg);
      transform: translateY(-2px);
    }

    .mg3p-card--interactive:active {
      transform: translateY(0);
      box-shadow: var(--shadow-md);
    }

    .mg3p-card--interactive:focus-visible {
      outline: 3px solid var(--color-border-focus);
      outline-offset: 2px;
    }

    /* ========================================
       ACCESSIBILITY
       ======================================== */
    
    @media (prefers-reduced-motion: reduce) {
      .mg3p-card {
        transition: none;
        transform: none !important;
      }
    }
  `]
})
export class CardComponent {
  /**
   * Título do card (opcional)
   */
  @Input() title?: string;

  /**
   * Subtítulo do card (opcional)
   */
  @Input() subtitle?: string;

  /**
   * Variante visual do card
   */
  @Input() variant: 'default' | 'elevated' | 'outlined' | 'flat' = 'default';

  /**
   * Padding interno do body
   */
  @Input() padding: 'none' | 'sm' | 'md' | 'lg' = 'md';

  /**
   * Se true, mostra slot de actions no header
   */
  @Input() hasActions = false;

  /**
   * Se true, mostra slot de footer
   */
  @Input() hasFooter = false;

  /**
   * Se true, adiciona estilos de hover/click
   */
  @Input() interactive = false;

  /**
   * Role ARIA (para acessibilidade)
   */
  @Input() role: string | null = null;

  /**
   * Classes CSS computadas
   */
  cardClasses = computed(() => {
    const classes = [
      'mg3p-card',
      `mg3p-card--${this.variant}`,
      `mg3p-card--padding-${this.padding}`,
    ];

    if (this.interactive) {
      classes.push('mg3p-card--interactive');
    }

    return classes.join(' ');
  });
}

// Made with Bob
